import React, {
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useState,
} from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-routing-machine";
import "leaflet-routing-machine/dist/leaflet-routing-machine.css";
import Styles from "./MapContainerLeaflet.module.css";
import toast from "react-hot-toast";

/* ---------- FIX LEAFLET DEFAULT ICON ISSUE ---------- */
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

/* ---------- ROUTING COMPONENT ---------- */
const Routing = React.memo(({ origin, destination, showDirections }) => {
  const map = useMap();
  const routingRef = useRef(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!map || !showDirections || !origin || !destination) return;

    // Remove previous routing safely
    if (routingRef.current && routingRef.current._map) {
      try {
        routingRef.current.getPlan().setWaypoints([]);
        map.removeControl(routingRef.current);
      } catch (e) {
        console.warn("Routing removal failed:", e);
      }
      routingRef.current = null;
    }

    setLoading(true); // start loader

    // Temporarily suppress OSRM demo server warning
    const originalWarn = console.warn;
    console.warn = (...args) => {
      if (
        typeof args[0] === "string" &&
        args[0].includes("You are using OSRM's demo server")
      ) {
        return; // ignore warning
      }
      originalWarn(...args);
    };

    // Create new routing control
    routingRef.current = L.Routing.control({
      waypoints: [
        L.latLng(origin.lat, origin.lng),
        L.latLng(destination.lat, destination.lng),
      ],
      lineOptions: { styles: [{ color: "#2684ff", weight: 5 }] },
      addWaypoints: false,
      draggableWaypoints: false,
      fitSelectedRoutes: true,
      show: false,
      createMarker: () => null,
    }).addTo(map);

    // Restore console.warn
    console.warn = originalWarn;

    // Listen to routing events
    routingRef.current.on("routesfound", () => setLoading(false));
    routingRef.current.on("routingerror", () => setLoading(false));

    // Cleanup
    return () => {
      if (routingRef.current && routingRef.current._map) {
        try {
          routingRef.current.getPlan().setWaypoints([]);
          map.removeControl(routingRef.current);
        } catch (e) {
          console.warn("Routing cleanup failed:", e);
        }
        routingRef.current = null;
      }
    };
  }, [map, origin, destination, showDirections]);

  // Loader overlay
  return loading ? (
    <div
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        // transform: "translateX(-50%)",
        zIndex: 9999,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        padding: "8px 12px",
        borderRadius: "6px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <p style={{ color: "white", fontWeight: "600", fontSize: "15px" }}>
        Fetching directions...
      </p>
    </div>
  ) : null;
});

/* ---------- AUTO FOCUS ---------- */
const AutoFocusLocation = React.memo(({ location, enableSelectLocation }) => {
  const map = useMap();
  const hasFocused = useRef(false);

  useEffect(() => {
    if (!map || enableSelectLocation || !location || hasFocused.current) return;

    map.flyTo([location.lat, location.lng], 16, {
      animate: true,
      duration: 1.5,
    });
    hasFocused.current = true;
  }, [map, location, enableSelectLocation]);

  return null;
});

/* ---------- CENTER MARKER SELECTION ---------- */
const CenterMarkerSelector = ({ enableSelectLocation, onSelectLocation }) => {
  const map = useMap();
  const markerRef = useRef(null);
  const debounceRef = useRef(null);
  const selectedDataRef = useRef(null);
  const confirmBtnRef = useRef(null);
  const locationBtnRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const fetchAddress = useCallback(async (lat, lng) => {
    try {
      setLoading(true);
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`,
      );
      const data = await res.json();
      const address = data?.address || {};
      const fullAddressRaw = data?.display_name || "";

      // Split address into parts
      let parts = fullAddressRaw.split(",").map((p) => p.trim());

      // Values you want to remove EXACTLY
      const removeValues = [
        address.postcode,
        address.city,
        address.state,
        address.country,
      ].filter(Boolean);

      // Remove only exact matches (case-insensitive)
      parts = parts.filter(
        (part) =>
          !removeValues.some(
            (remove) => part.toLowerCase() === remove.toLowerCase(),
          ),
      );

      const cleanedAddress = parts.join(", ");

      selectedDataRef.current = {
        lat,
        lng,
        // fullAddress: data?.display_name || "",
        // address: data?.address || {},
        fullAddress: cleanedAddress,
        address,
      };

      if (confirmBtnRef.current) confirmBtnRef.current.style.display = "block";
    } catch (err) {
      console.error("Reverse geocode failed", err);
    } finally {
      setLoading(false); // hide loader
    }
  }, []);

  useEffect(() => {
    if (!map || !enableSelectLocation) return;

    const center = map.getCenter();
    markerRef.current = L.marker(center, { interactive: false }).addTo(map);

    // const handleMove = () => markerRef.current.setLatLng(map.getCenter());
    const handleMove = () => {
      const newCenter = map.getCenter();
      markerRef.current.setLatLng(newCenter);

      // Clear old data and hide confirm button while moving
      selectedDataRef.current = null;
      if (confirmBtnRef.current) confirmBtnRef.current.style.display = "none";
    };
    const handleMoveEnd = () => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const c = map.getCenter();
        fetchAddress(c.lat, c.lng);
      }, 500);
    };

    map.on("move", handleMove);
    map.on("moveend", handleMoveEnd);

    /* ---------------- CONFIRM BUTTON ---------------- */
    const ConfirmControl = L.Control.extend({
      onAdd: () => {
        const btn = L.DomUtil.create("button", "leaflet-bar leaflet-control");
        btn.innerHTML = "✔";
        btn.title = "Confirm Location";
        Object.assign(btn.style, {
          width: "30px",
          height: "30px",
          fontSize: "15px",
          cursor: "pointer",
          background: "#28a745",
          color: "#fff",
          borderRadius: "20px",
          display: "none",
        });

        L.DomEvent.on(btn, "click", (e) => {
          L.DomEvent.stopPropagation(e);
          if (selectedDataRef.current) {
            onSelectLocation?.(selectedDataRef.current);
            btn.style.display = "none";
          }
        });

        confirmBtnRef.current = btn;
        return btn;
      },
    });
    const confirmControl = new ConfirmControl({ position: "bottomright" });
    map.addControl(confirmControl);

    /* ---------------- CURRENT LOCATION BUTTON ---------------- */
    const LocationControl = L.Control.extend({
      onAdd: () => {
        const btn = L.DomUtil.create("button", "leaflet-bar leaflet-control");
        btn.innerHTML = "📍";
        btn.title = "Get Current Location";
        Object.assign(btn.style, {
          width: "30px",
          height: "30px",
          fontSize: "15px",
          cursor: "pointer",
          borderRadius: "20px",
          background: "#fff",
        });

        L.DomEvent.on(btn, "click", (e) => {
          L.DomEvent.stopPropagation(e);
          if (!navigator.geolocation)
            return toast("Geolocation not supported", { type: "error" });

          navigator.geolocation.getCurrentPosition(
            (position) => {
              const lat = position.coords.latitude;
              const lng = position.coords.longitude;
              map.flyTo([lat, lng], 16);
              markerRef.current.setLatLng([lat, lng]);
              fetchAddress(lat, lng);
            },
            () => toast("Unable to fetch location", { type: "error" }),
          );
        });

        locationBtnRef.current = btn;
        return btn;
      },
    });
    const locationControl = new LocationControl({ position: "topright" });
    map.addControl(locationControl);

    return () => {
      map.off("move", handleMove);
      map.off("moveend", handleMoveEnd);
      if (markerRef.current) map.removeLayer(markerRef.current);
      map.removeControl(confirmControl);
      map.removeControl(locationControl);
      clearTimeout(debounceRef.current);
    };
  }, [map, enableSelectLocation, fetchAddress, onSelectLocation]);

  return loading ? (
    <div
      style={{
        position: "absolute",
        top: 10,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9999,
        backgroundColor: "rgba(0,0,0,0.6)",
        color: "white",
        padding: "6px 12px",
        borderRadius: "6px",
        fontWeight: 600,
        fontSize: "14px",
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <p style={{ color: "white", fontWeight: "600", fontSize: "15px" }}>
        Fetching Location...
      </p>
    </div>
  ) : null;
};

/* ---------- MAIN COMPONENT ---------- */
function MapContainerLeaflet({
  origin,
  location,
  showDirections = false,
  enableSelectLocation = false,
  onSelectLocation,
}) {
  const destination = useMemo(
    () =>
      location
        ? { lat: Number(location.lat), lng: Number(location.lng) }
        : null,
    [location],
  );

  const center = useMemo(
    () => origin ?? destination ?? { lat: 11.0168, lng: 76.9558 },
    [origin, destination],
  );

  return (
    <MapContainer
      center={[center.lat, center.lng]}
      zoom={15}
      className={Styles.mapContainer}
    >
      <TileLayer
        attribution="&copy; OpenStreetMap contributors"
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      <AutoFocusLocation
        location={destination}
        enableSelectLocation={enableSelectLocation}
      />

      {!enableSelectLocation && destination && (
        <Marker position={[destination.lat, destination.lng]} />
      )}
      {!enableSelectLocation && origin && (
        <Marker position={[origin.lat, origin.lng]} />
      )}
      {!enableSelectLocation && (
        <Routing
          origin={origin}
          destination={destination}
          showDirections={showDirections}
        />
      )}

      <CenterMarkerSelector
        enableSelectLocation={enableSelectLocation}
        onSelectLocation={onSelectLocation}
      />
    </MapContainer>
  );
}

export default React.memo(MapContainerLeaflet);
